<template>
  <div class="flex -mx-6" :class="order.status === 'Cancelled' ? 'opacity-50 pointer-events-none' : ''">
    <div class="w-full md:w-2/3 px-6">
      <h3 class="mb-2 text-sm">Order Details</h3>

      <hr class="border-b my-6" />

      <div class="flex flex-wrap -mx-6">
        <div class="w-full md:w-1/2 px-6">
          <h4 class="mb-2 text-sm">Pack Details:</h4>
          <p>{{ selectedOffice.network_id === 2 ? 'Marketing Pack' : 'Enhanced Marketing' }} <strong>'{{ order.pack.name }}'</strong></p>
          <p class="capitalize">{{ order.pack.delivery_type }} Delivery</p>

          <div class="mt-6">
            <p>Order Number: {{ order.id }}</p>
          </div>

          <div v-if="order.payment_type_id !== 0">
            <hr class="border-b my-6" />

            <h4 class="mb-2 text-sm">Payment:</h4>
            <div v-for="payment in order.payments" :key="payment.id" class="mb-2">
              <p v-if="payment.type_id === 1">Payment from the vendor ({{ payment.email }})</p>
              <p v-else>Payment from {{ payment.email }}</p>
              <p v-if="payment.paid_at">Payment of {{ payment.amount | gbp }} received at: {{ payment.paid_at | datetime }}</p>
              <p v-else><strong>Payment of {{ payment.amount | gbp }} pending</strong></p>
            </div>
          </div>

          <loader v-if="deliveryAddresses === null" class="mt-6" />
          <div v-else-if="deliverableItems.length > 0" class="mt-6">
            <h3 class="mb-2 text-sm">Print Delivery Addresses:</h3>
            <div v-for="(item, i) in deliverableItems" :key="i" class="mb-3" :class="{'hover:underline cursor-pointer': !item.item.is_printed}" @click="selectAddressLookupItem(item.item)">
              <strong class="block">{{ item.item.name }}</strong>
              {{ item.deliveryAddress.address }}<span v-if="!item.item.is_printed"> | <i class="fas fa-pencil-alt"></i> Edit</span>
            </div>

            <hr class="border-b my-6" />
          </div>
        </div>

        <div class="w-full md:w-1/2 px-6">
          <h4 class="mb-2 text-sm">Contact and Property Details:</h4>
          <div class="mb-6">
            <p>{{ order.vendor1.name }}</p>
            <p>{{ order.vendor1.phone }}</p>
            <p>{{ order.vendor1.email }}</p>
          </div>

          <div class="mb-6" v-if="order.vendor2">
            <p>{{ order.vendor2.name }}</p>
            <p>{{ order.vendor2.phone }}</p>
            <p>{{ order.vendor2.email }}</p>
          </div>

          <div class="mb-6">
            <p>{{ order.address }}</p>
            <p class="text-xs text-grey-darker">
              <span v-if="order.bedrooms">{{ order.bedrooms }} {{ order.bedrooms === 1 ? 'Bedroom' : 'Bedrooms' }} | </span>
              <span v-if="order.bathrooms">{{ order.bathrooms }} {{ order.bathrooms === 1 ? 'Bathroom' : 'Bathrooms' }} | </span>
              <span v-if="order.reception_rooms">{{ order.reception_rooms }} {{ order.reception_rooms === 1 ? 'Reception Room' : 'Reception Rooms' }} | </span>
              <span class="block">{{ order.price_qualifier }} {{ order.guide_price | gbp }}</span>
              <span v-if="order.tenure" class="block">Tenure: {{ order.tenure }}</span>
              <span v-if="order.council_tax_band" class="block">Council Tax Band: {{ order.council_tax_band }}</span>
            </p>
          </div>

          <hr class="border-b" />
          <h4 class="mb-2 text-sm">Additional Info:</h4>
          <p class="italic mb-6">{{ order.move_reason }}</p>
          <p class="whitespace-pre-line">{{ order.notes }}</p>
          <p v-if="order.edition_number && order.edition_number !== 'TBD'">
            Refined Magazine: {{ order.edition_number}}.
          </p>
          <p v-if="order.delivery_date && order.delivery_date !== 'TBD'">
            Estimated delivery date: {{order.delivery_date}}.
          </p>
        </div>
      </div>
    </div>

    <!-- ORDER SUMMARY -->
    <div class="w-full md:w-1/3 px-6 border-l text-grey-darkest">
      <h3 class="text-black">Order Summary</h3>
      <table class="w-full mb-6">
        <tbody>
          <tr>
            <td class="align-top pb-3">
              {{ selectedOffice.network_id === 2 ? 'Marketing Pack' : 'Enhanced Marketing Pack' }}
            </td>
            <td class="text-right align-top pb-3">
              {{ order.pack.price | gbp }}
            </td>
          </tr>

          <tr v-for="(item, i) in orderItems" :key="i">
            <td class="align-top text-xs">
              <ul class="pl-6">
                <li>
                  {{ item.name }}
                  <span v-if="item.data">
                    <span v-if="item.data.name"> - {{ item.data.name }}</span>
                    <span v-if="item.data.style"> ({{ item.data.style }})</span>
                    <span v-if="item.data.location_maps_included"> - Location Maps Included</span>
                    <span class="text-grey-dark" v-if="item.data.copies">
                      {{ item.data.copies }} {{ item.data.copies === 1 ? 'Copy' : 'Copies' }}
                    </span>
                    <span class="text-grey-dark" v-if="item.data.additional_copies">
                      - +{{ item.data.additional_copies }} additional copies
                    </span>
                    <span class="text-grey-dark" v-if="item.data.location_maps_included">
                      - Location Maps Included
                    </span>
                    <span class="text-grey-dark" v-if="item.data.double_page_spread">
                      (Double Page Spread Upgrade)
                    </span>
                  </span>
                </li>
              </ul>
            </td>
            <td class="text-right align-top text-xs">
              <span v-if="item.price">{{ item.price | gbp }}</span>
            </td>
          </tr>

          <tr v-if="order.price.ehouse_additional_charges">
            <td class="align-top text-xs">
              <ul class="pl-6">
                <li>
                  ehouse Additional Charges
                </li>
              </ul>
            </td>
            <td class="text-right align-top text-xs">
              {{ order.price.ehouse_additional_charges | gbp }}
            </td>
          </tr>
        </tbody>
      </table>

      <hr class="border-b" />

      <!-- TOTAL PRICE -->
      <table class="w-full">
        <tbody>
          <tr>
            <td class="font-bold pb-1">Subtotal</td>
            <td class="text-right pb-1">{{ order.price.subtotal | gbp }}</td>
          </tr>
          <tr>
            <td class="font-bold pb-3">VAT</td>
            <td class="text-right pb-3">{{ order.price.vat | gbp }}</td>
          </tr>
          <tr class="text-black font-bold text-sm">
            <td>TOTAL</td>
            <td class="text-right">{{ order.price.total | gbp }}</td>
          </tr>
        </tbody>
      </table>
      <strong v-if="refinedOrderItem" class="mt-2">Plus Refined Magazine delivery, which will be invoiced seperately</strong>
      <!-- PAYMENT -->
      <div v-if="order.payment.amount" class="bg-grey-lighter mt-6 -mx-6 p-6">
        <table class="w-full">
          <tbody>
            <tr v-for="payment in order.payments" :key="payment.id">
              <td v-if="payment.paid_at" class="pb-1">{{ payment.type_id === 2 || payment.type_id === 3 ? 'Agent paid' : 'Received' }}</td>
              <td v-else-if="payment.type_id === 1" class="pb-1">To collect from vendor</td>
              <td v-else class="pb-1">Pending payment</td>
              <td class="text-right pb-1">{{ payment.amount | gbp }}</td>
            </tr>
            <tr v-if="order.payment.amount > order.price.total">
              <td>Agent</td>
              <td class="text-right text-green-dark font-bold">+ {{ order.payment.amount - order.price.total | gbp }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <delivery-address-modal v-if="addressLookupItem" @leave="addressLookupItem = null" @change="updateDeliveryAddress" :name="addressLookupItem.name" v-model="addressLookupItem.delivery_address_id" :deliveryAddresses="deliveryAddresses" />
  </div>
</template>

<script>
import { filter, find } from 'lodash'
import api from '@/http'
import DeliveryAddressModal from '../../../components/DeliveryAddressModal.vue'
import { mapState } from 'vuex'

export default {
  components: { DeliveryAddressModal },
  props: ['order'],
  computed: {
    ...mapState(['selectedOffice']),
    orderItems () {
      return filter(this.order.items, (item) => item.fulfillment_by_id !== 0)
    },
    refinedOrderItem () {
      return find(this.order.items, (item) => item.internal_type_id === 10)
    },
    deliverableItems () {
      if (!this.deliveryAddresses) {
        return []
      }

      let output = []
      const items = filter(this.order.items, (item) => item.is_deliverable)

      for (let i = 0; i < items.length; i++) {
        output.push({
          item: items[i],
          deliveryAddress: find(this.deliveryAddresses, (deliveryAddress) => deliveryAddress.id === items[i].delivery_address_id) || find(this.deliveryAddresses, (deliveryAddress) => deliveryAddress.id === null)
        })
      }

      return output
    }
  },
  data () {
    return {
      deliveryAddresses: null,
      addressLookupItem: null
    }
  },
  created () {
    this.loadDeliveryAddresses()
  },
  methods: {
    loadDeliveryAddresses () {
      return api.get('delivery-addresses', {
        params: {
          office_id: this.order.office_id
        }
      }).then((response) => {
        this.deliveryAddresses = response.data
      })
    },
    selectAddressLookupItem (item) {
      if (item.is_printed || !item.is_deliverable) {
        return
      }

      this.addressLookupItem = item
    },
    updateDeliveryAddress () {
      api.patch(`/marketing-packs/orders/${this.order.id}/items/${this.addressLookupItem.id}`, {
        delivery_address_id: this.addressLookupItem.delivery_address_id
      })
    },
    paymentLabel (payment) {
      if (payment.is_additional) {
        return 'Additional Payment'
      }

      if (payment.type === 'vendor') {
        return 'Vendor Payment'
      }

      return 'Agent Payment'
    }
  }
}
</script>
